import { Component, inject, OnInit, Renderer2, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { PriceStore } from './store/price.store';
import { PrimengExportsModule } from './shared/primeng-exports/primeng-exports.module';
import { HOME_PAGE_TABS } from './core/constants/home-page-tabs.const';
import { TransactionStore } from './store/transaction.store';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { PosthogService } from './core/services/posthog.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, PrimengExportsModule, TranslocoPipe],
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly translocoService = inject(TranslocoService);
  private readonly priceStore = inject(PriceStore);
  private readonly transactionStore = inject(TransactionStore);
  private readonly renderer = inject(Renderer2);

  tabs = signal(HOME_PAGE_TABS.map((tab) => ({ ...tab, title: this.translocoService.translate(tab.title) })));

  // Show the page title + section tabs only on final-tier views (dashboard / price); hide them while
  // on the drill-down chain and plantation pages.
  protected readonly showShellHeader = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => !this.isDrillDownView(event.urlAfterRedirects))
    ),
    { initialValue: !this.isDrillDownView(this.router.url) }
  );

  // Highlight the tab matching the current route so refresh and drill-back (→ /transaction/multi-tier)
  // land on the right tab.
  protected readonly activeTabIndex = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => this.matchTabIndex(event.urlAfterRedirects))
    ),
    { initialValue: this.matchTabIndex(this.router.url) }
  );

constructor() {
  inject(PosthogService);
}

  ngOnInit(): void {
    this.updateBodyOverflow();
  }

  onTabChange(index: number | string | undefined) {
    if (index == null) return;
    this.router.navigateByUrl(`/${this.tabs()[index as number].route}`);
    this.priceStore.resetLoadingStates();
    this.transactionStore.resetLoadingStates();
  }

  private isDrillDownView(url: string): boolean {
    return /\/transaction\/[^/?]+\/(chain|plantations)/.test(url);
  }

  // Match the current URL to a tab, most-specific route first (transaction/multi-tier before transaction).
  private matchTabIndex(url: string): number {
    const path = url.split(/[?#]/)[0];
    const match = this.tabs()
      .map((tab, index) => ({ index, route: tab.route }))
      .sort((a, b) => b.route.length - a.route.length)
      .find((tab) => path.includes(tab.route));
    return match?.index ?? 0;
  }

  private updateBodyOverflow(): void {
    const isEmbedded = window.self !== window.top;
    this.renderer.setStyle(document.body, 'overflow', isEmbedded ? 'hidden' : 'auto');
  }
}
